import React from "react";
import { useTranslation } from "react-i18next";

import Input from "@/components/input";
import { LanguageToggle } from "@/components/language-toggle";
import { PlacesAutocomplete } from "@/components/places-autocomplete";
import { AppErrorBoundary } from "@/components/ErrorBoundary";
import { isApiError } from "@/queries/api/apiUtils";
import { notify } from "@/utils";
import { prepareImageForUpload, validateImageFile } from "@/utils/image-upload";
import { Logger } from "@/utils/logger";
import { RESTAURANT_TYPE_GROUPS } from "@/constants/ownerOnboarding";
import { uploadOwnerRestaurantImage } from "@/queries/api/ownerOnboardingApi";

import { useOwnerOnboarding } from "../-owner-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

export function OwnerRestaurantStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const { data, patch, setRestaurantImageUrl, saveDraft } = useOwnerOnboarding();
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      if (!data.restaurantName.trim()) {
        notify.error(t("ownerRestaurant.nameRequired"));
        return false;
      }
      if (!data.restaurantType.trim()) {
        notify.error(t("ownerRestaurant.typeRequired"));
        return false;
      }
      if (!data.restaurantAddress?.formattedAddress.trim()) {
        notify.error(t("ownerRestaurant.addressRequired"));
        return false;
      }
      if (data.websiteUrl.trim()) {
        try {
          new URL(data.websiteUrl.trim());
        } catch {
          notify.error(t("ownerRestaurant.websiteInvalid"));
          return false;
        }
      }
      try {
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("owner onboarding save failed at restaurant", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("ownerRestaurant.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, data, saveDraft, t]);

  const handleImage = async (file: File) => {
    setUploading(true);
    try {
      let prepared: File;
      try {
        prepared = await prepareImageForUpload(file);
      } catch (e) {
        Logger.error("restaurant image conversion failed", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(t("ownerRestaurant.imageTypeError"));
        return;
      }
      const valid = validateImageFile(prepared);
      if (!valid.ok) {
        notify.error(
          t(
            valid.reason === "size"
              ? "ownerRestaurant.imageTooLarge"
              : "ownerRestaurant.imageTypeError",
          ),
        );
        return;
      }
      const url = await uploadOwnerRestaurantImage(prepared);
      setRestaurantImageUrl(url);
      notify.success(t("ownerRestaurant.imageUploaded"));
    } catch (e) {
      Logger.error("restaurant image upload failed", {
        error: e instanceof Error ? e.message : String(e),
        status: isApiError(e) ? e.status : undefined,
      });
      if (isApiError(e) && e.status === 413) {
        notify.error(t("ownerRestaurant.imageTooLargeServer"));
      } else {
        notify.error(t("ownerRestaurant.imageUploadFailed"));
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="w-full flex flex-col gap-5">
      <div>
        <h2 className="heading-1">{t("ownerRestaurant.title")}</h2>
        <p className="body-small text-gray-600 mt-2">
          {t("ownerRestaurant.description")}
        </p>
      </div>

      <section className="flex flex-col gap-3 mt-2">
        <h3 className="text-xl font-semibold text-garnet">
          {t("languagePreference.title")}
        </h3>
        <p className="text-sm text-garnet/80">
          {t("languagePreference.description")}
        </p>
        <LanguageToggle variant="card" persistToBackend />
      </section>

      <div className="border-garnet/25 w-full border my-2" />

      <div className="flex flex-col gap-4 max-w-[480px]">
        <Input
          label={t("ownerRestaurant.nameLabel")}
          placeholder={t("ownerRestaurant.namePlaceholder")}
          value={data.restaurantName}
          onChange={(e) => patch({ restaurantName: e.currentTarget.value })}
        />
        <div className="flex flex-col gap-1">
          <label className="body-small text-gray-700" htmlFor="restaurant-type">
            {t("ownerRestaurant.typeLabel")}
          </label>
          <select
            id="restaurant-type"
            value={data.restaurantType}
            onChange={(e) => patch({ restaurantType: e.currentTarget.value })}
            className="rounded-md border border-garnet/40 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-garnet"
          >
            <option value="">{t("ownerRestaurant.typePlaceholder")}</option>
            {RESTAURANT_TYPE_GROUPS.map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <AppErrorBoundary
            fallback={
              <Input
                label={t("ownerRestaurant.addressLabel")}
                placeholder={t("ownerRestaurant.addressPlaceholder")}
                value={data.restaurantAddress?.formattedAddress ?? ""}
                onChange={(e) =>
                  patch({
                    restaurantAddress: {
                      formattedAddress: e.currentTarget.value,
                    },
                  })
                }
              />
            }
          >
            <PlacesAutocomplete
              label={t("ownerRestaurant.addressLabel")}
              placeholder={t("ownerRestaurant.addressPlaceholder")}
              value={data.restaurantAddress?.formattedAddress ?? ""}
              onChange={(e) => {
                const next = (e.target as HTMLInputElement).value;
                // Track raw input so the autocomplete can fetch predictions;
                // the structured fields get filled in on selection.
                patch({
                  restaurantAddress: {
                    ...(data.restaurantAddress ?? { formattedAddress: "" }),
                    formattedAddress: next,
                  },
                });
              }}
              onPlaceSelect={(place) => {
                if (!place) return;
                const formatted = place.formattedAddress ?? place.name ?? "";
                patch({
                  restaurantAddress: {
                    formattedAddress: formatted,
                    // Street / city / state / ZIP from Google, used for billing sales tax.
                    address1: place.components?.address1 ?? formatted,
                    city: place.components?.city,
                    state: place.components?.state,
                    zipcode: place.components?.zipcode,
                    latitude: place.location?.lat,
                    longitude: place.location?.lng,
                  },
                });
              }}
            />
          </AppErrorBoundary>
        </div>
        <div className="flex flex-col gap-1">
          <span className="body-small text-gray-700">
            {t("ownerRestaurant.photoLabel")}
          </span>
          {data.restaurantImageUrl ? (
            <img
              src={data.restaurantImageUrl}
              alt={t("ownerRestaurant.photoAlt")}
              className="w-32 h-32 object-cover rounded-md ring-1 ring-garnet"
            />
          ) : null}
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) void handleImage(f);
              e.currentTarget.value = "";
            }}
          />
          {uploading && (
            <span className="body-small">{t("ownerRestaurant.uploading")}</span>
          )}
        </div>
        <Input
          label={t("ownerRestaurant.websiteLabel")}
          placeholder={t("ownerRestaurant.websitePlaceholder")}
          value={data.websiteUrl}
          onChange={(e) => patch({ websiteUrl: e.currentTarget.value })}
        />
      </div>
    </section>
  );
}
